import { createContext, useContext, type ReactNode } from 'react';
import type { Language } from '@shared/constants';

const pt = {
  appName: 'Voz Livre',
  loading: 'Carregando…',
  close: 'Fechar',
  cancel: 'Cancelar',
  save: 'Salvar',
  delete: 'Excluir',
  edit: 'Editar',
  done: 'Concluir',
  back: 'Voltar',
  confirm: 'Confirmar',
  retry: 'Tentar novamente',

  // Communication bar
  speak: 'Falar',
  deleteWord: 'Apagar palavra',
  clearAll: 'Apagar tudo',
  settings: 'Configurações',
  sentenceEmpty: 'Toque nos símbolos',
  sentenceLabel: 'Frase',
  removeFromSentence: 'Remover “{word}” da frase',

  // Navigation
  home: 'Início',
  keyboard: 'Teclado',
  newCategory: 'Nova',
  categoriesNav: 'Categorias',
  recent: 'Recentes',
  openCategory: 'Abrir {name}',

  // Grid / editing
  addSymbol: 'Adicionar',
  editMode: 'Editar',
  editModeOn: 'Concluir edição',
  moveUp: 'Mover para antes',
  moveDown: 'Mover para depois',
  emptyCategory: 'Esta categoria ainda não tem símbolos. Toque em “Adicionar”.',
  editCategory: 'Editar categoria',
  deleteCategory: 'Excluir categoria',

  // Keyboard
  typeHere: 'Digite aqui…',
  typedText: 'Texto digitado',
  space: 'Espaço',
  backspaceLetter: 'Apagar letra',
  addToSentence: 'Pôr na frase',
  speakTyped: 'Falar texto',
  clearTyped: 'Limpar texto',

  // Symbol editor
  newSymbolTitle: 'Novo símbolo',
  editSymbolTitle: 'Editar símbolo',
  symbolInCategory: 'Categoria: {name}',
  wordPt: 'Palavra (português)',
  wordEn: 'Palavra (inglês, opcional)',
  chooseImage: 'Imagem',
  emojiTab: 'Emoji',
  photoTab: 'Foto',
  takePhoto: 'Tirar foto',
  originalTab: 'Original',
  restoreOriginal: 'Restaurar original',
  speakOnTap: 'Falar cada símbolo ao tocar',
  speakOnTapHint: 'A palavra é falada na hora. O botão Falar continua lendo a frase inteira.',
  frequent: 'Frequentes',
  chooseFromGallery: 'Escolher da galeria',
  imageFailed: 'Não foi possível usar esta imagem. Tente outra foto.',
  processingImage: 'Processando imagem…',
  removePhoto: 'Remover foto',
  wordRequired: 'Digite a palavra.',
  confirmDeleteSymbol: 'Excluir o símbolo “{word}”?',

  // Category editor
  newCategoryTitle: 'Nova categoria',
  editCategoryTitle: 'Editar categoria',
  categoryName: 'Nome da categoria',
  categoryIcon: 'Ícone',
  categoryColor: 'Cor',
  categoryNameRequired: 'Digite um nome para a categoria.',
  categoryNameTaken: 'Já existe uma categoria com este nome.',
  confirmDeleteCategory: 'Excluir a categoria “{name}”?',
  confirmDeleteCategoryWithSymbols: 'Excluir a categoria “{name}” e os {count} símbolo(s) dela? Esta ação não pode ser desfeita.',
  moveCategoryLeft: 'Mover categoria para a esquerda',
  moveCategoryRight: 'Mover categoria para a direita',

  // Settings
  settingsTitle: 'Configurações',
  voiceSection: 'Voz',
  voiceGender: 'Tipo de voz',
  female: 'Feminina',
  male: 'Masculina',
  voiceChoice: 'Voz do dispositivo',
  voiceAuto: 'Automática',
  noVoices: 'Nenhuma voz encontrada para este idioma neste dispositivo. A voz padrão será usada.',
  voiceSpeed: 'Velocidade da fala',
  testVoice: 'Testar voz',
  testPhrase: 'Olá! Esta é a minha voz.',
  displaySection: 'Exibição',
  gridSize: 'Colunas da grade',
  columns: '{n} colunas',
  gridHint: 'Em telas pequenas o número de colunas é reduzido automaticamente.',
  language: 'Idioma',
  syncNote: 'As configurações ficam salvas na sua conta e valem em todos os seus aparelhos. A voz escolhida depende de cada aparelho.',
  attribution: 'Símbolos: Microsoft Fluent Emoji (licença MIT) e Mulberry Symbols © Steve Lee (mulberrysymbols.org, licença CC BY-SA 4.0).',

  // Account menu
  profile: 'Meu perfil',
  admin: 'Administração',
  logout: 'Sair',
  accountMenu: 'Menu da conta',

  // Plans
  planTrial: 'Período grátis',
  planTrialDays: '{n} dias grátis',
  planStandard: 'Standard',
  planTest: 'Conta de teste',
  planFree: 'Plano gratuito',
  subscribe: 'Assinar',
  upgradeTitle: 'Assinar o Voz Livre',
  trialEndedTitle: 'Seu período de teste terminou',
  upgradeText: 'Com a assinatura você tem a voz e todos os recursos do aplicativo.',
  trialEndedText: 'No plano gratuito a fala está desativada. Os símbolos e o teclado continuam funcionando. Assine para voltar a usar a voz.',
  perMonth: '/mês',
  subscribeNow: 'Assinar agora',
  notNow: 'Agora não',
  billingDisabled: 'Pagamentos ainda não estão disponíveis. Fale com o administrador.',
  paymentSuccess: 'Assinatura ativada. Obrigado por apoiar o Voz Livre!',
  paymentIssue: 'Há um problema com o pagamento da assinatura. Verifique o cartão no seu perfil.',
  voiceUnavailable: 'Este aparelho não tem síntese de voz disponível.',

  // Caregiver area
  adjust: 'Ajustes',
  adultArea: 'Área do responsável',
  enterPin: 'PIN do responsável',
  createPinIntro: 'Crie um PIN de 4 dígitos. Ele protege os ajustes contra toques sem querer.',
  newPin: 'Novo PIN',
  repeatPin: 'Repita o PIN',
  confirmPin: 'Confirmar PIN',
  needOnline: 'Conecte-se à internet para desbloquear.',
  editBoard: 'Editar quadro',
  voiceAndScreen: 'Voz e tela',
  lock: 'Bloquear',
  editingBanner: 'Modo edição',
  editingHomeHint: 'Os Frequentes se montam sozinhos com o uso. Escolha uma categoria para editar.',
  voiceLocked: 'Voz desativada. Peça ao responsável.',

  // Offline
  offline: 'Sem internet — o quadro continua funcionando.',
  pendingSync: 'Alterações aguardando sincronização',
  syncError: 'Alterações ainda não salvas. Tentando de novo…',

  // Hide / show ("Ocultar")
  hide: 'Ocultar',
  show: 'Mostrar',
  hiddenTag: 'Oculto',
  hideCategory: 'Ocultar categoria',
  showCategory: 'Mostrar categoria',
  hideAllSymbols: 'Ocultar todos',
  showAllSymbols: 'Mostrar todos',
  hiddenHint: 'Símbolos ocultos não aparecem para a criança. O espaço deles fica vazio, para os outros não mudarem de lugar.',
  categoryHiddenNote: 'Esta categoria está oculta: a criança não a vê.',

  // Visual routine
  routine: 'Rotina',
  routineNew: 'Nova rotina',
  routineEdit: 'Editar rotina',
  routineDelete: 'Excluir rotina',
  routineName: 'Nome da rotina',
  routineNamePlaceholder: 'Ex.: Manhã, Escola, Hora de dormir',
  routineKind: 'Tipo',
  routineSequence: 'Passo a passo',
  routineSequenceHint: 'Uma sequência de até {n} passos, um depois do outro.',
  routineFirstThen: 'Primeiro → Depois',
  routineFirstThenHint: 'Uma tarefa e, depois dela, a recompensa.',
  routineSteps: 'Passos',
  routineNoSteps: 'Nenhum passo ainda.',
  routinePick: 'Toque nos símbolos para adicionar os passos:',
  routinePickFirst: 'Toque no símbolo do “Primeiro”:',
  routinePickThen: 'Agora toque no símbolo do “Depois”:',
  routineFull: 'Todos os passos já foram escolhidos. Remova um para trocar.',
  routineNameRequired: 'Digite um nome para a rotina.',
  routineStepsRequired: 'Adicione pelo menos um passo.',
  routineFirstThenRequired: 'Escolha os dois símbolos: o do “Primeiro” e o do “Depois”.',
  removeStep: 'Remover passo {n}',
  first: 'Primeiro',
  then: 'Depois',
  routineNow: 'Agora',
  routineRestart: 'Recomeçar',
  routineDone: 'Rotina concluída! Muito bem!',
  routineIntro: 'Monte a rotina do dia com os símbolos do quadro. A criança vê o que está fazendo agora e o que vem depois, e marca cada passo ao terminar.',
  routineMissingSteps: 'Os símbolos desta rotina foram excluídos. Edite a rotina para escolher outros.',
  routineChoose: 'Escolher rotina',
  routineStepDone: '{word}, feito',
  confirmDeleteRoutine: 'Excluir a rotina “{name}”?',
  moveRoutineLeft: 'Mover rotina para a esquerda',
  moveRoutineRight: 'Mover rotina para a direita',
};

type Dict = typeof pt;

const en: Dict = {
  appName: 'Voz Livre',
  loading: 'Loading…',
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  edit: 'Edit',
  done: 'Done',
  back: 'Back',
  confirm: 'Confirm',
  retry: 'Try again',

  speak: 'Speak',
  deleteWord: 'Delete word',
  clearAll: 'Clear all',
  settings: 'Settings',
  sentenceEmpty: 'Tap the symbols',
  sentenceLabel: 'Sentence',
  removeFromSentence: 'Remove “{word}” from the sentence',

  home: 'Home',
  keyboard: 'Keyboard',
  newCategory: 'New',
  categoriesNav: 'Categories',
  recent: 'Recent',
  openCategory: 'Open {name}',

  addSymbol: 'Add',
  editMode: 'Edit',
  editModeOn: 'Finish editing',
  moveUp: 'Move earlier',
  moveDown: 'Move later',
  emptyCategory: 'This category has no symbols yet. Tap “Add”.',
  editCategory: 'Edit category',
  deleteCategory: 'Delete category',

  typeHere: 'Type here…',
  typedText: 'Typed text',
  space: 'Space',
  backspaceLetter: 'Delete letter',
  addToSentence: 'Add to sentence',
  speakTyped: 'Speak text',
  clearTyped: 'Clear text',

  newSymbolTitle: 'New symbol',
  editSymbolTitle: 'Edit symbol',
  symbolInCategory: 'Category: {name}',
  wordPt: 'Word (Portuguese)',
  wordEn: 'Word (English, optional)',
  chooseImage: 'Image',
  emojiTab: 'Emoji',
  photoTab: 'Photo',
  takePhoto: 'Take a photo',
  originalTab: 'Original',
  restoreOriginal: 'Restore original',
  speakOnTap: 'Speak each symbol when tapped',
  speakOnTapHint: 'The word is spoken right away. The Speak button still reads the whole sentence.',
  frequent: 'Frequent',
  chooseFromGallery: 'Choose from gallery',
  imageFailed: 'This image could not be used. Try another photo.',
  processingImage: 'Processing image…',
  removePhoto: 'Remove photo',
  wordRequired: 'Type the word.',
  confirmDeleteSymbol: 'Delete the symbol “{word}”?',

  newCategoryTitle: 'New category',
  editCategoryTitle: 'Edit category',
  categoryName: 'Category name',
  categoryIcon: 'Icon',
  categoryColor: 'Colour',
  categoryNameRequired: 'Type a name for the category.',
  categoryNameTaken: 'A category with this name already exists.',
  confirmDeleteCategory: 'Delete the category “{name}”?',
  confirmDeleteCategoryWithSymbols: 'Delete the category “{name}” and its {count} symbol(s)? This cannot be undone.',
  moveCategoryLeft: 'Move category left',
  moveCategoryRight: 'Move category right',

  settingsTitle: 'Settings',
  voiceSection: 'Voice',
  voiceGender: 'Voice type',
  female: 'Female',
  male: 'Male',
  voiceChoice: 'Device voice',
  voiceAuto: 'Automatic',
  noVoices: 'No voice found for this language on this device. The default voice will be used.',
  voiceSpeed: 'Speech rate',
  testVoice: 'Test voice',
  testPhrase: 'Hello! This is my voice.',
  displaySection: 'Display',
  gridSize: 'Grid columns',
  columns: '{n} columns',
  gridHint: 'On small screens the number of columns is reduced automatically.',
  language: 'Language',
  syncNote: 'Settings are saved to your account and apply on all your devices. The chosen voice depends on each device.',
  attribution: 'Symbols: Microsoft Fluent Emoji (MIT licence) and Mulberry Symbols © Steve Lee (mulberrysymbols.org, CC BY-SA 4.0 licence).',

  profile: 'My profile',
  admin: 'Administration',
  logout: 'Sign out',
  accountMenu: 'Account menu',

  planTrial: 'Free trial',
  planTrialDays: '{n} trial days left',
  planStandard: 'Standard',
  planTest: 'Test account',
  planFree: 'Free plan',
  subscribe: 'Subscribe',
  upgradeTitle: 'Subscribe to Voz Livre',
  trialEndedTitle: 'Your trial has ended',
  upgradeText: 'A subscription gives you speech and every feature of the app.',
  trialEndedText: 'Speech is disabled on the free plan. Symbols and the keyboard keep working. Subscribe to use the voice again.',
  perMonth: '/month',
  subscribeNow: 'Subscribe now',
  notNow: 'Not now',
  billingDisabled: 'Payments are not available yet. Please contact the administrator.',
  paymentSuccess: 'Subscription active. Thank you for supporting Voz Livre!',
  paymentIssue: 'There is a problem with your subscription payment. Check your card in your profile.',
  voiceUnavailable: 'Speech synthesis is not available on this device.',

  adjust: 'Settings',
  adultArea: 'Caregiver area',
  enterPin: 'Caregiver PIN',
  createPinIntro: 'Create a 4-digit PIN. It protects settings from accidental taps.',
  newPin: 'New PIN',
  repeatPin: 'Repeat the PIN',
  confirmPin: 'Confirm PIN',
  needOnline: 'Connect to the internet to unlock.',
  editBoard: 'Edit board',
  voiceAndScreen: 'Voice and screen',
  lock: 'Lock',
  editingBanner: 'Editing',
  editingHomeHint: 'Frequent fills itself with use. Choose a category to edit.',
  voiceLocked: 'Voice is off. Ask your caregiver.',

  offline: 'Offline — the board keeps working.',
  pendingSync: 'Changes waiting to sync',
  syncError: 'Changes not saved yet. Retrying…',

  hide: 'Hide',
  show: 'Show',
  hiddenTag: 'Hidden',
  hideCategory: 'Hide category',
  showCategory: 'Show category',
  hideAllSymbols: 'Hide all',
  showAllSymbols: 'Show all',
  hiddenHint: 'Hidden symbols are not shown to the child. Their space stays empty, so the others do not move.',
  categoryHiddenNote: 'This category is hidden: the child does not see it.',

  routine: 'Routine',
  routineNew: 'New routine',
  routineEdit: 'Edit routine',
  routineDelete: 'Delete routine',
  routineName: 'Routine name',
  routineNamePlaceholder: 'E.g. Morning, School, Bedtime',
  routineKind: 'Type',
  routineSequence: 'Step by step',
  routineSequenceHint: 'A sequence of up to {n} steps, one after the other.',
  routineFirstThen: 'First → Then',
  routineFirstThenHint: 'A task and, after it, the reward.',
  routineSteps: 'Steps',
  routineNoSteps: 'No steps yet.',
  routinePick: 'Tap the symbols to add the steps:',
  routinePickFirst: 'Tap the symbol for “First”:',
  routinePickThen: 'Now tap the symbol for “Then”:',
  routineFull: 'All steps are chosen. Remove one to change it.',
  routineNameRequired: 'Type a name for the routine.',
  routineStepsRequired: 'Add at least one step.',
  routineFirstThenRequired: 'Choose both symbols: “First” and “Then”.',
  removeStep: 'Remove step {n}',
  first: 'First',
  then: 'Then',
  routineNow: 'Now',
  routineRestart: 'Start over',
  routineDone: 'Routine done! Well done!',
  routineIntro: 'Build the day’s routine with the board’s symbols. The child sees what they are doing now and what comes next, and ticks each step when done.',
  routineMissingSteps: 'This routine’s symbols were deleted. Edit the routine to choose others.',
  routineChoose: 'Choose routine',
  routineStepDone: '{word}, done',
  confirmDeleteRoutine: 'Delete the routine “{name}”?',
  moveRoutineLeft: 'Move routine left',
  moveRoutineRight: 'Move routine right',
};

export const dictionaries: Record<Language, Dict> = { pt, en };
export type MessageKey = keyof Dict;

export function translate(language: Language, key: MessageKey, vars?: Record<string, string | number>): string {
  let text = dictionaries[language][key] ?? dictionaries.pt[key];
  if (vars) for (const [k, v] of Object.entries(vars)) text = text.replaceAll(`{${k}}`, String(v));
  return text;
}

export function detectLanguage(): Language {
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'pt';
}

const LanguageContext = createContext<Language>('pt');

export function LanguageProvider({ language, children }: { language: Language; children: ReactNode }) {
  return <LanguageContext.Provider value={language}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): Language {
  return useContext(LanguageContext);
}

export function useT() {
  const language = useContext(LanguageContext);
  return (key: MessageKey, vars?: Record<string, string | number>) => translate(language, key, vars);
}
